import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { letterPresetForRound } from "@/lib/disputePlan";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileCheck2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Potentially Negative","Collections","Charge-Offs","Late Payments","Repossessions","Student Loans","Bankruptcies","Public Records","Positive",
];

const LETTER_OPTIONS = [
  { key: "equifax", label: "Equifax Bureau Letter" },
  { key: "experian", label: "Experian Bureau Letter" },
  { key: "transunion", label: "TransUnion Bureau Letter" },
  { key: "furnisher", label: "Direct Furnisher Dispute" },
  { key: "debt_validation", label: "Debt Validation Request" },
  { key: "mov", label: "Method of Verification (MOV)" },
  { key: "follow_up", label: "Follow-Up Dispute" },
  { key: "arbitration", label: "Intent-to-Arbitrate Notice" },
];

function badgeFor(cat) {
  if (cat === "Positive") return "badge-success";
  if (["Collections","Charge-Offs","Repossessions","Bankruptcies","Public Records"].includes(cat)) return "badge-danger";
  return "badge-warning";
}

export default function Review() {
  const { id } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [retired, setRetired] = useState([]);
  const [selections, setSelections] = useState({}); // { accountId: {selected, reason, explanation, aiUsed} }
  const [searchParams] = useSearchParams();
  const [letters, setLetters] = useState(() => letterPresetForRound(Number(searchParams.get("round"))) || { equifax: true, experian: true, transunion: true, furnisher: false, debt_validation: false, mov: false, follow_up: false, arbitration: false });
  const [confirming, setConfirming] = useState(false);
  const [affirmingAi, setAffirmingAi] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/reports/${id}`), api.get("/settings/public")]).then(([r, s]) => {
      setReport(r.data);
      const uniqReasons = Array.from(new Set(s.data.reasons || []));
      setReasons(uniqReasons);
      setRetired(s.data.legacy_retired || []);
      setAiEnabled(!!s.data.ai_suggestions_enabled);
      const init = {};
      (r.data.accounts || []).forEach(a => { init[a.id] = { selected: false, reason: uniqReasons[0], explanation: "", aiUsed: false }; });
      setSelections(init);
    }).catch(()=> toast.error("Could not load report"));
  }, [id]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(CATEGORIES.map(c=>[c, []]));
    (report?.accounts || []).forEach(a => { (g[a.category] || g["Potentially Negative"]).push(a); });
    return g;
  }, [report]);

  const selectedList = useMemo(() => Object.entries(selections).filter(([,v])=>v.selected).map(([k])=>k), [selections]);
  const selectedCount = selectedList.length;

  const toggle = (aid, val) => setSelections(s=>({ ...s, [aid]: { ...s[aid], selected: val } }));
  const setReason = (aid, val) => setSelections(s=>({ ...s, [aid]: { ...s[aid], reason: val } }));
  const setExpl = (aid, val) => setSelections(s=>({ ...s, [aid]: { ...s[aid], explanation: val } }));
  const applySuggestion = (aid, txt) => setSelections(s=>({ ...s, [aid]: { ...s[aid], explanation: txt, aiUsed: true } }));

  const anyAiUsed = useMemo(() => Object.values(selections).some(v => v?.selected && v?.aiUsed), [selections]);

  const generate = async () => {
    if (!confirming) return toast.error("Please confirm the accuracy statement first");
    if (anyAiUsed && !affirmingAi) return toast.error("Please affirm the AI-assisted wording is accurate to your knowledge");
    if (selectedCount === 0) return toast.error("Select at least one account");
    const activeLetters = Object.entries(letters).filter(([,v])=>v).map(([k])=>k);
    if (activeLetters.length === 0) return toast.error("Choose at least one letter type");

    setGenerating(true);
    try {
      const payload = {
        report_id: id,
        letter_types: activeLetters,
        selections: selectedList.map(aid => ({ account_id: aid, reason: selections[aid].reason, explanation: selections[aid].explanation })),
      };
      const r = await api.post("/documents/generate", payload);
      toast.success(`Generated ${r.data.documents.length} document(s)`);
      nav("/app/documents");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  if (!report) return <AppShell><div className="text-slate-500">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Step 2 · Review & Select</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Credit Report Review</h1>
          <p className="text-slate-500 text-sm mt-1">
            Bureau: <span className="text-slate-800">{report.bureau}</span> · Accounts: <span className="text-slate-800">{report.accounts?.length || 0}</span>
          </p>
        </div>
        <div className="flex gap-3">
          <span className="stage-pill rounded-full px-3 py-1.5 text-sm" data-testid="review-selected-count">{selectedCount} selected</span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {CATEGORIES.filter(c=>grouped[c].length).map(cat => (
            <div key={cat} data-testid={`review-cat-${cat.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-slate-900 text-lg font-semibold">{cat}</h3>
                <span className="text-xs text-slate-500">{grouped[cat].length} accounts</span>
              </div>
              <div className="space-y-3">
                {grouped[cat].map(a => (
                  <Card key={a.id} className="glass rounded-2xl hover-lift">
                    <CardContent className="p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-slate-900 font-semibold">{a.creditor}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Acct {a.account_number_masked} · {a.account_type} · <span className={`inline-block rounded-full px-2 py-0.5 ml-1 text-[10px] uppercase tracking-widest ${badgeFor(a.category)}`}>{a.category}</span></div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-800">
                          <Checkbox checked={!!selections[a.id]?.selected} onCheckedChange={(v)=>toggle(a.id, !!v)} data-testid={`review-include-${a.id}`} />
                          Include in dispute
                        </label>
                      </div>
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <Info k="Status" v={a.account_status}/>
                        <Info k="Balance" v={a.balance}/>
                        <Info k="Past Due" v={a.past_due}/>
                        <Info k="Reported" v={a.date_reported}/>
                        <Info k="Opened" v={a.date_opened}/>
                        <Info k="Last Payment" v={a.last_payment_date}/>
                        <Info k="Remarks" v={a.remarks} span={2}/>
                      </div>
                      {selections[a.id]?.selected && (
                        <div className="mt-4 rounded-xl bg-slate-50/70 border border-slate-200 p-4 space-y-3">
                          <div className="text-xs uppercase tracking-widest text-purple-700">Dispute reason</div>
                          <Select value={selections[a.id]?.reason} onValueChange={(v)=>setReason(a.id, v)}>
                            <SelectTrigger data-testid={`review-reason-select-${a.id}`} className="bg-slate-50 border-slate-200 text-slate-900">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white text-slate-900 border-slate-200">
                              {reasons.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Textarea placeholder="Explain the issue in your own words (optional)…" value={selections[a.id]?.explanation} onChange={(e)=>setExpl(a.id, e.target.value)} data-testid={`review-explanation-${a.id}`} className="bg-slate-50 border-slate-200 text-slate-900" />
                          <p className="text-[11px] text-slate-500" data-testid={`review-pii-notice-${a.id}`}>Do not include your Social Security number, full account number, date of birth, or other sensitive information.</p>
                          {aiEnabled && (
                            <AiSuggestPanel
                              accountId={a.id}
                              reportId={id}
                              explanation={selections[a.id]?.explanation || ""}
                              onUse={(txt)=>applySuggestion(a.id, txt)}
                              aiUsed={!!selections[a.id]?.aiUsed}
                            />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1 space-y-6">
          <Card className="glass rounded-2xl sticky top-24">
            <CardContent className="p-6">
              <div className="flex items-center gap-2"><Wand2 size={16} className="text-purple-700"/><h3 className="text-slate-900 font-semibold">Generate Letters</h3></div>
              <p className="text-xs text-slate-500 mt-1">Select the document types to create for your selected accounts.</p>
              <div className="mt-4 space-y-2">
                {LETTER_OPTIONS.filter(l => !retired.includes(l.key)).map(l => (
                  <label key={l.key} className="flex items-center gap-2 text-sm text-slate-800">
                    <Checkbox checked={!!letters[l.key]} onCheckedChange={(v)=>setLetters(s=>({...s, [l.key]: !!v}))} data-testid={`review-letter-${l.key}`} />
                    {l.label}
                  </label>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-slate-50/70 border border-slate-200 p-3">
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <Checkbox checked={confirming} onCheckedChange={(v)=>setConfirming(!!v)} data-testid="review-confirm-checkbox" />
                  I confirm the selected items are inaccurate, incomplete, outdated, duplicated, or unverifiable.
                </label>
                {anyAiUsed && (
                  <label className="flex items-start gap-2 text-xs text-slate-700 mt-2" data-testid="review-ai-affirm-wrap">
                    <Checkbox checked={affirmingAi} onCheckedChange={(v)=>setAffirmingAi(!!v)} data-testid="review-ai-affirm-checkbox" />
                    I confirm the wording I used from the AI assistant is accurate to my own knowledge.
                  </label>
                )}
              </div>
              <Button disabled={generating} onClick={generate} data-testid="review-generate-button" className="btn-primary rounded-xl w-full mt-4 py-5">
                {generating ? <><Loader2 className="animate-spin mr-2" size={16}/>Generating…</> : <><FileCheck2 className="mr-2" size={16}/>Generate Documents</>}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function Info({ k, v, span = 1 }) {
  return (
    <div className={`rounded-lg bg-slate-50/50 border border-slate-200 p-2 ${span===2?"col-span-2 sm:col-span-2":""}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div>
      <div className="text-slate-800 mt-0.5 truncate">{v || "—"}</div>
    </div>
  );
}

function AiSuggestPanel({ reportId, accountId, explanation, onUse, aiUsed }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [clarifying, setClarifying] = useState("");
  const [error, setError] = useState("");
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [dismissed, setDismissed] = useState({});

  const call = async () => {
    setError(""); setSuggestions([]); setClarifying(""); setDismissed({});
    if (!explanation || explanation.trim().length < 40) {
      setError("Please write at least a few sentences describing what happened before asking for suggestions.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/disputes/suggest-reasons", { report_id: reportId, account_id: accountId, explanation });
      if (r.data.clarifying_question) setClarifying(r.data.clarifying_question);
      else setSuggestions(r.data.suggestions || []);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) setError("Too many suggestion requests. Please wait a few minutes.");
      else if (code === 413) setError("Explanation is too long. Please shorten it to under 2000 characters.");
      else setError("Suggestions unavailable — please try again in a moment or continue manually.");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3" data-testid={`ai-suggest-panel-${accountId}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-700">
          <b>AI assistant (optional).</b> Draft suggestions from your own words. Not legal advice.
        </div>
        <Button type="button" onClick={call} disabled={loading} variant="outline" className="rounded-xl border-purple-200 bg-white text-purple-700 whitespace-nowrap" data-testid={`ai-suggest-btn-${accountId}`}>
          {loading ? <><Loader2 className="animate-spin mr-1" size={14}/>Thinking…</> : (suggestions.length || clarifying ? "Regenerate" : "Suggest dispute reasons")}
        </Button>
      </div>
      {error && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid={`ai-suggest-error-${accountId}`}>{error}</div>}
      {clarifying && (
        <div className="mt-2 text-xs text-slate-800 bg-white border border-purple-200 rounded-lg p-2" data-testid={`ai-suggest-clarify-${accountId}`}>
          <b>Question from the assistant:</b> {clarifying}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="mt-2 space-y-2" data-testid={`ai-suggest-list-${accountId}`}>
          <div className="text-[11px] text-slate-500">Draft suggestions — for your review only. Verify every fact before mailing. Editing is encouraged.</div>
          {suggestions.map((s, i) => dismissed[i] ? null : (
            <div key={i} className="rounded-lg bg-white border border-slate-200 p-2 text-xs text-slate-800">
              {editingIdx === i ? (
                <>
                  <Textarea rows={2} value={editText} onChange={(e)=>setEditText(e.target.value)} className="bg-slate-50 border-slate-200 text-slate-900 text-xs" data-testid={`ai-suggest-edit-textarea-${accountId}-${i}`}/>
                  <div className="mt-1 flex gap-2">
                    <Button size="sm" onClick={()=>{ onUse(editText); setEditingIdx(null); }} className="btn-primary rounded-lg text-xs" data-testid={`ai-suggest-use-edited-${accountId}-${i}`}>Use edited</Button>
                    <Button size="sm" variant="outline" onClick={()=>setEditingIdx(null)} className="rounded-lg text-xs">Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  <div>{s}</div>
                  <div className="mt-1 flex gap-2">
                    <Button size="sm" onClick={()=>onUse(s)} className="btn-primary rounded-lg text-xs" data-testid={`ai-suggest-use-${accountId}-${i}`}>Use</Button>
                    <Button size="sm" variant="outline" onClick={()=>{ setEditingIdx(i); setEditText(s); }} className="rounded-lg text-xs" data-testid={`ai-suggest-edit-${accountId}-${i}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={()=>setDismissed(d=>({...d, [i]: true}))} className="rounded-lg text-xs text-slate-500" data-testid={`ai-suggest-ignore-${accountId}-${i}`}>Ignore</Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {aiUsed && <div className="mt-2 text-[11px] text-emerald-700" data-testid={`ai-suggest-used-badge-${accountId}`}>✅ AI-assisted text is now in your explanation. Edit freely; nothing is submitted until you generate.</div>}
    </div>
  );
}
